<script setup>
import { reactive, ref, getCurrentInstance } from 'vue';
import Dialog from 'primevue/dialog';
import Button from 'primevue/button';
import InputText from 'primevue/inputtext';
import InputMask from 'primevue/inputmask';
import axios from 'axios';

const { proxy } = getCurrentInstance();
const $t = proxy.$t;

const props = defineProps({
    visible: { type: Boolean, required: true },
    submitting: { type: Boolean, default: false },
});

const emit = defineEmits(['update:visible', 'confirm']);

const fetchingZip = ref(false);

const form = reactive({
    first_name: '',
    last_name: '',
    cpf: '',
    zip: '',
    street: '',
    number: '',
    neighborhood: '',
    city: '',
    state: '',
});

const errors = reactive({});

const validate = () => {
    Object.keys(errors).forEach(k => delete errors[k]);
    const required = ['first_name', 'last_name', 'cpf', 'zip', 'street', 'number', 'neighborhood', 'city', 'state'];
    required.forEach(field => {
        if (!form[field]?.trim()) errors[field] = $t('Required');
    });
    const cpfClean = form.cpf.replace(/\D/g, '');
    if (cpfClean && cpfClean.length !== 11) errors.cpf = $t('Invalid CPF');
    return Object.keys(errors).length === 0;
};

const fetchAddress = async () => {
    const zip = form.zip.replace(/\D/g, '');
    if (zip.length !== 8) return;
    fetchingZip.value = true;
    try {
        const { data } = await axios.get(`https://viacep.com.br/ws/${zip}/json/`);
        if (!data.erro) {
            form.street = data.logradouro || '';
            form.neighborhood = data.bairro || '';
            form.city = data.localidade || '';
            form.state = data.uf || '';
        }
    } catch {
        // silently fail — user fills manually
    } finally {
        fetchingZip.value = false;
    }
};

const confirm = () => {
    if (!validate()) return;
    emit('confirm', { ...form });
};

const close = () => emit('update:visible', false);
</script>

<template>
    <Dialog
        :visible="visible"
        @update:visible="$emit('update:visible', $event)"
        modal
        :closable="!submitting"
        :style="{ width: '520px' }"
        :header="$t('Payer Information')"
    >
        <div class="flex flex-col gap-4 py-2">
            <p class="text-sm text-surface-600">
                {{ $t('Boleto requires your personal and address details for issuance.') }}
            </p>

            <!-- Nome -->
            <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium">{{ $t('First name') }} *</label>
                    <InputText v-model="form.first_name" :class="{ 'p-invalid': errors.first_name }" />
                    <small v-if="errors.first_name" class="text-red-500">{{ errors.first_name }}</small>
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium">{{ $t('Last name') }} *</label>
                    <InputText v-model="form.last_name" :class="{ 'p-invalid': errors.last_name }" />
                    <small v-if="errors.last_name" class="text-red-500">{{ errors.last_name }}</small>
                </div>
            </div>

            <!-- CPF -->
            <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">CPF *</label>
                <InputMask v-model="form.cpf" mask="999.999.999-99" :class="{ 'p-invalid': errors.cpf }" />
                <small v-if="errors.cpf" class="text-red-500">{{ errors.cpf }}</small>
            </div>

            <!-- CEP -->
            <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">CEP *</label>
                <div class="flex gap-2">
                    <InputMask
                        v-model="form.zip"
                        mask="99999-999"
                        class="flex-1"
                        :class="{ 'p-invalid': errors.zip }"
                        @blur="fetchAddress"
                    />
                    <Button
                        icon="pi pi-search"
                        severity="secondary"
                        :loading="fetchingZip"
                        @click="fetchAddress"
                    />
                </div>
                <small v-if="errors.zip" class="text-red-500">{{ errors.zip }}</small>
            </div>

            <!-- Endereço -->
            <div class="flex flex-col gap-1">
                <label class="text-sm font-medium">{{ $t('Street') }} *</label>
                <InputText v-model="form.street" :class="{ 'p-invalid': errors.street }" />
                <small v-if="errors.street" class="text-red-500">{{ errors.street }}</small>
            </div>

            <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium">{{ $t('Number') }} *</label>
                    <InputText v-model="form.number" :class="{ 'p-invalid': errors.number }" />
                    <small v-if="errors.number" class="text-red-500">{{ errors.number }}</small>
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium">{{ $t('Neighborhood') }} *</label>
                    <InputText v-model="form.neighborhood" :class="{ 'p-invalid': errors.neighborhood }" />
                    <small v-if="errors.neighborhood" class="text-red-500">{{ errors.neighborhood }}</small>
                </div>
            </div>

            <div class="grid grid-cols-2 gap-3">
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium">{{ $t('City') }} *</label>
                    <InputText v-model="form.city" :class="{ 'p-invalid': errors.city }" />
                    <small v-if="errors.city" class="text-red-500">{{ errors.city }}</small>
                </div>
                <div class="flex flex-col gap-1">
                    <label class="text-sm font-medium">{{ $t('State') }} *</label>
                    <InputText v-model="form.state" maxlength="2" :class="{ 'p-invalid': errors.state }" />
                    <small v-if="errors.state" class="text-red-500">{{ errors.state }}</small>
                </div>
            </div>
        </div>

        <template #footer>
            <div class="flex justify-between items-center w-full">
                <Button
                    :label="$t('Cancel')"
                    severity="secondary"
                    text
                    :disabled="submitting"
                    @click="close"
                />
                <Button
                    :label="$t('Generate Boleto')"
                    icon="pi pi-barcode"
                    :loading="submitting"
                    @click="confirm"
                />
            </div>
        </template>
    </Dialog>
</template>
